import { useEffect, useRef, useState } from 'react';
import { Link } from '../router';

import { api, ApiError } from '../api';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { Notice } from '../components/Notice';
import { PageHeader } from '../components/PageHeader';
import type { Conversation } from '../types';
import { displayAddress, formatRelative } from '../types';

type Mailbox = 'inbox' | 'archive' | 'trash';
const pageSize = 50;
const maximumRefreshChunk = 100;

export function InboxPage() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [search, setSearch] = useState('');
  const [mailbox, setMailbox] = useState<Mailbox>('inbox');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [error, setError] = useState('');
  const scopeVersion = useRef(0);
  const loadedCount = useRef(0);
  const windowRevision = useRef(0);
  const refreshController = useRef<AbortController | null>(null);
  const loadMoreController = useRef<AbortController | null>(null);

  useEffect(() => {
    const version = ++scopeVersion.current;
    windowRevision.current += 1;
    loadedCount.current = 0;
    loadMoreController.current?.abort();
    loadMoreController.current = null;
    setConversations([]);
    setNextOffset(null);
    setLoadingMore(false);
    setLoading(true);
    let requestActive = false;
    const load = async (showLoading: boolean) => {
      if (requestActive || loadMoreController.current || document.visibilityState !== 'visible') return;
      requestActive = true;
      const controller = new AbortController();
      refreshController.current = controller;
      if (showLoading) setLoading(true);
      setError('');
      const revision = windowRevision.current;
      const targetCount = showLoading ? pageSize : Math.max(loadedCount.current, pageSize);
      try {
        const fresh: Conversation[] = [];
        const seen = new Set<string>();
        let offset = 0;
        let freshNextOffset: number | null = 0;
        while (fresh.length < targetCount && freshNextOffset !== null) {
          if (controller.signal.aborted || scopeVersion.current !== version || revision !== windowRevision.current) return;
          const limit = Math.min(maximumRefreshChunk, targetCount - fresh.length);
          const result = await api<{ conversations: Conversation[]; nextOffset: number | null }>(`/api/conversations?mailbox=${mailbox}&search=${encodeURIComponent(search)}&limit=${limit}${offset > 0 ? `&offset=${offset}` : ''}`, { signal: controller.signal });
          if (controller.signal.aborted || scopeVersion.current !== version || revision !== windowRevision.current) return;
          for (const item of result.conversations) {
            if (!seen.has(item.id)) { seen.add(item.id); fresh.push(item); }
          }
          freshNextOffset = result.nextOffset;
          if (freshNextOffset !== null && freshNextOffset <= offset) break;
          if (freshNextOffset !== null) offset = freshNextOffset;
        }
        loadedCount.current = fresh.length;
        setConversations(fresh);
        setNextOffset(freshNextOffset);
      } catch (caught) {
        if ((caught as Error).name !== 'AbortError' && scopeVersion.current === version) setError(caught instanceof ApiError ? caught.message : 'Could not load messages.');
      } finally {
        requestActive = false;
        if (refreshController.current === controller) refreshController.current = null;
        if (scopeVersion.current === version) setLoading(false);
      }
    };
    const initialTimer = window.setTimeout(() => void load(true), search ? 220 : 0);
    const pollTimer = window.setInterval(() => void load(false), 10_000);
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') void load(false); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      if (scopeVersion.current === version) scopeVersion.current += 1;
      refreshController.current?.abort();
      refreshController.current = null;
      loadMoreController.current?.abort();
      loadMoreController.current = null;
      window.clearTimeout(initialTimer);
      window.clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [search, mailbox]);

  const title = mailbox === 'archive' ? 'Archived' : mailbox === 'trash' ? 'Trash' : 'Messages';

  async function loadMore() {
    if (nextOffset === null || loadMoreController.current) return;
    const controller = new AbortController();
    const version = scopeVersion.current;
    refreshController.current?.abort();
    loadMoreController.current = controller;
    windowRevision.current += 1;
    setLoadingMore(true); setError('');
    try {
      const result = await api<{ conversations: Conversation[]; nextOffset: number | null }>(`/api/conversations?mailbox=${mailbox}&search=${encodeURIComponent(search)}&limit=${pageSize}&offset=${nextOffset}`, { signal: controller.signal });
      if (controller.signal.aborted || scopeVersion.current !== version) return;
      setConversations((current) => {
        const merged = [...current, ...result.conversations.filter((item) => !current.some((existing) => existing.id === item.id))];
        loadedCount.current = merged.length;
        return merged;
      });
      setNextOffset(result.nextOffset);
    } catch (caught) {
      if ((caught as Error).name !== 'AbortError' && scopeVersion.current === version) setError(caught instanceof ApiError ? caught.message : 'Could not load older conversations.');
    } finally {
      if (loadMoreController.current === controller) {
        loadMoreController.current = null;
        if (scopeVersion.current === version) setLoadingMore(false);
      }
    }
  }

  return (
    <div className="page page-inbox">
      <PageHeader title={title} description={mailbox === 'trash' ? 'Restore synced conversations or delete their server copies permanently.' : 'Synced conversations from devices you chose to connect.'} actions={<Link className="button button-primary" to="/app/compose"><Icon name="edit" /> New message</Link>} />
      <div className="inbox-toolbar">
        <label className="search-field"><Icon name="search" /><span className="sr-only">Search messages</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search conversations" /><button className="icon-button" type="button" aria-label="Clear search" disabled={!search} onClick={() => setSearch('')}><Icon name="close" size={18} /></button></label>
        <div className="mailbox-tabs" aria-label="Conversation mailboxes">
          <button className={`button button-quiet ${mailbox === 'inbox' ? 'selected' : ''}`} type="button" onClick={() => setMailbox('inbox')}><Icon name="inbox" /> Inbox</button>
          <button className={`button button-quiet ${mailbox === 'archive' ? 'selected' : ''}`} type="button" onClick={() => setMailbox('archive')}><Icon name="archive" /> Archived</button>
          <button className={`button button-quiet ${mailbox === 'trash' ? 'selected' : ''}`} type="button" onClick={() => setMailbox('trash')}><Icon name="delete" /> Trash</button>
        </div>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {loading ? <div className="skeleton-list" aria-label="Loading messages">{[1, 2, 3].map((item) => <span key={item} />)}</div> : conversations.length === 0 ? (
        <EmptyState icon={search ? 'search_off' : mailbox === 'archive' ? 'inventory_2' : mailbox === 'trash' ? 'delete' : 'forum'} title={search ? 'No matches' : mailbox === 'archive' ? 'Nothing archived' : mailbox === 'trash' ? 'Trash is empty' : 'Your web inbox is ready'} body={search ? 'Try a different name, number, or message.' : mailbox === 'archive' ? 'Archived conversations will appear here.' : mailbox === 'trash' ? 'Trashed server copies are deleted automatically after 30 days.' : 'Enable optional sync in the Android app and your conversations will appear here.'} action={!search && mailbox === 'inbox' && <Link className="button button-tonal" to="/app/devices">Check connected devices</Link>} />
      ) : (
        <div className="conversation-list">
          {conversations.map((conversation) => <Link className="conversation-row" to={`/app/conversations/${conversation.id}`} key={conversation.id}>
            <span className="avatar conversation-avatar">{displayAddress(conversation).slice(0, 1).toUpperCase()}</span>
            <span className="conversation-copy"><span><strong>{displayAddress(conversation)}</strong><time>{formatRelative(conversation.last_message_at)}</time></span><span><small>{conversation.last_message_direction === 'outgoing' && 'You: '}{conversation.last_message_body || 'No message preview'}</small>{conversation.last_message_status === 'failed' && <Icon name="error" size={17} />}</span></span>
            <span className="conversation-meta">{conversation.pinned && <Icon name="keep" filled size={17} />}{conversation.unread_count > 0 && <b>{conversation.unread_count > 99 ? '99+' : conversation.unread_count}</b>}</span>
          </Link>)}
          {nextOffset !== null && <div className="load-more-row"><button className="button button-tonal" type="button" disabled={loadingMore} onClick={() => void loadMore()}>{loadingMore ? 'Loading…' : 'Load older conversations'}</button></div>}
        </div>
      )}
    </div>
  );
}
